import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { CreditCard, Check, Tag, Trash2, Plus, Gem } from "lucide-react";

const NUM_FIELDS = [
  { k: "maxProposals", label: "Propositions" },
  { k: "maxUsers", label: "Utilisateurs" },
  { k: "aiGenerationsPerMonth", label: "Gén. IA/mois" },
  { k: "mcpRatePerMinute", label: "MCP req/min" },
  { k: "maxApiTokens", label: "Clés API" },
  { k: "trialDays", label: "Jours d'essai" },
];

const PLAN_LABELS = {
  pro: "Pro",
  business: "Business",
  enterprise: "Enterprise",
};

export default function AdminBilling() {
  const [form, setForm] = useState({
    stripe_price_pro_monthly: "",
    stripe_price_pro_yearly: "",
    stripe_price_business_monthly: "",
    stripe_price_business_yearly: "",
    stripe_price_enterprise_monthly: "",
    stripe_price_enterprise_yearly: "",
    stripe_billing_webhook_secret: "",
  });
  const [stripeKeySet, setStripeKeySet] = useState(false);
  const [webhookSecretSet, setWebhookSecretSet] = useState(false);
  const [saving, setSaving] = useState(false);

  // Forfaits dynamiques
  const [plans, setPlans] = useState([]);
  const [planDrafts, setPlanDrafts] = useState({}); // tier -> champs édités
  const [savingPlan, setSavingPlan] = useState(null);
  const [showNewPlan, setShowNewPlan] = useState(false);
  const [newPlan, setNewPlan] = useState({ tier: "", label: "", priceLabel: "", maxProposals: -1, maxUsers: -1, aiGenerationsPerMonth: -1, mcpRatePerMinute: 0, maxApiTokens: 0, trialDays: 0, features: "proposals, signature, pdf", isPublic: true });

  const loadPlans = () => api.get("/admin/plans").then(({ data }) => {
    setPlans(data);
    const drafts = {};
    data.forEach((p) => { drafts[p.tier] = { ...p, features: (p.features || []).join(", ") }; });
    setPlanDrafts(drafts);
  }).catch(() => {});

  const draftField = (tier, k, v) => setPlanDrafts((d) => ({ ...d, [tier]: { ...d[tier], [k]: v } }));

  const savePlan = async (tier) => {
    const d = planDrafts[tier];
    setSavingPlan(tier);
    try {
      await api.put(`/admin/plans/${tier}`, {
        label: d.label, priceLabel: d.priceLabel,
        maxProposals: Number(d.maxProposals), maxUsers: Number(d.maxUsers),
        aiGenerationsPerMonth: Number(d.aiGenerationsPerMonth), mcpRatePerMinute: Number(d.mcpRatePerMinute),
        maxApiTokens: Number(d.maxApiTokens),
        trialDays: Number(d.trialDays), isPublic: !!d.isPublic,
        features: String(d.features).split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success(`Forfait ${tier} enregistré`);
      loadPlans();
    } catch (e) { toast.error(e.response?.data?.message || "Erreur"); }
    finally { setSavingPlan(null); }
  };

  const deletePlan = async (tier) => {
    if (!window.confirm(`Supprimer le forfait "${tier}" ?`)) return;
    try { await api.delete(`/admin/plans/${tier}`); toast.success("Forfait supprimé"); loadPlans(); }
    catch (e) { toast.error(e.response?.data?.message || "Erreur"); }
  };

  const createPlan = async () => {
    if (!newPlan.tier.trim() || !newPlan.label.trim()) return toast.error("Identifiant et nom requis");
    try {
      await api.post("/admin/plans", {
        ...newPlan,
        tier: newPlan.tier.toLowerCase().trim(),
        maxProposals: Number(newPlan.maxProposals), maxUsers: Number(newPlan.maxUsers),
        aiGenerationsPerMonth: Number(newPlan.aiGenerationsPerMonth), mcpRatePerMinute: Number(newPlan.mcpRatePerMinute),
        maxApiTokens: Number(newPlan.maxApiTokens),
        trialDays: Number(newPlan.trialDays),
        features: String(newPlan.features).split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Forfait créé");
      setShowNewPlan(false);
      setNewPlan({ tier: "", label: "", priceLabel: "", maxProposals: -1, maxUsers: -1, aiGenerationsPerMonth: -1, mcpRatePerMinute: 0, maxApiTokens: 0, trialDays: 0, features: "proposals, signature, pdf", isPublic: true });
      loadPlans();
    } catch (e) { toast.error(e.response?.data?.message || "Erreur"); }
  };

  // Codes promo
  const [promos, setPromos] = useState([]);
  const [promoForm, setPromoForm] = useState({ code: "", plan: "pro", durationMonths: 12, maxUses: 0, description: "", discountType: "", discountValue: 0 });
  const [addingPromo, setAddingPromo] = useState(false);
  const [showPromoForm, setShowPromoForm] = useState(false);

  const loadPromos = () => api.get("/admin/promo-codes").then(({ data }) => setPromos(data)).catch(() => {});
  const deletePromo = async (code) => {
    if (!window.confirm(`Supprimer le code ${code} ?`)) return;
    try { await api.delete(`/admin/promo-codes/${code}`); toast.success("Supprimé"); loadPromos(); }
    catch (e) { toast.error("Erreur"); }
  };
  const createPromo = async () => {
    if (!promoForm.code.trim()) return toast.error("Code requis");
    setAddingPromo(true);
    try {
      await api.post("/admin/promo-codes", { ...promoForm, code: promoForm.code.toUpperCase().trim() });
      toast.success("Code créé");
      setPromoForm({ code: "", plan: "pro", durationMonths: 12, maxUses: 0, description: "", discountType: "", discountValue: 0 });
      setShowPromoForm(false);
      loadPromos();
    } catch (e) { toast.error(e.response?.data?.message || "Erreur"); }
    finally { setAddingPromo(false); }
  };

  useEffect(() => {
    loadPromos();
    loadPlans();
    api.get("/admin/stripe").then(({ data }) => {
      setStripeKeySet(!!data.stripe_api_key_set);
      setWebhookSecretSet(!!data.stripe_billing_webhook_secret_set);
      setForm((f) => ({
        ...f,
        stripe_price_pro_monthly: data.stripe_price_pro_monthly || "",
        stripe_price_pro_yearly: data.stripe_price_pro_yearly || "",
        stripe_price_business_monthly: data.stripe_price_business_monthly || "",
        stripe_price_business_yearly: data.stripe_price_business_yearly || "",
        stripe_price_enterprise_monthly: data.stripe_price_enterprise_monthly || "",
        stripe_price_enterprise_yearly: data.stripe_price_enterprise_yearly || "",
      }));
    }).catch(() => {});
  }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.stripe_billing_webhook_secret) delete payload.stripe_billing_webhook_secret;
      await api.put("/admin/stripe", payload);
      toast.success("Configuration Stripe enregistrée");
      if (form.stripe_billing_webhook_secret) {
        setWebhookSecretSet(true);
        setForm((f) => ({ ...f, stripe_billing_webhook_secret: "" }));
      }
    } catch (e) {
      toast.error(e.response?.data?.message || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 md:p-8 lg:p-12 max-w-3xl">
      <div className="mb-8">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-2">Administration</p>
        <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight">Facturation</h1>
      </div>

      {/* Forfaits dynamiques */}
      <div className="bg-card border border-border rounded-sm p-6 mb-6 space-y-5">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Gem className="w-5 h-5 text-primary" />
            <h3 className="font-heading text-lg font-semibold">Forfaits</h3>
          </div>
          <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setShowNewPlan((v) => !v)}>
            <Plus className="w-4 h-4 mr-1" /> Nouveau forfait
          </Button>
        </div>
        <p className="text-sm text-muted-foreground">Limites, prix, jours d'essai et features de chaque forfait. <code className="font-mono text-xs bg-muted px-1 rounded">-1</code> = illimité. Les changements s'appliquent immédiatement à l'enforcement.</p>

        {showNewPlan && (
          <div className="border border-dashed border-primary/40 rounded-sm p-4 space-y-3 bg-primary/5" data-testid="new-plan-form">
            <div className="grid sm:grid-cols-3 gap-3">
              <div><Label className="text-xs">Identifiant (slug)</Label><Input value={newPlan.tier} onChange={(e) => setNewPlan({ ...newPlan, tier: e.target.value })} placeholder="agency" className="mt-1 rounded-sm font-mono text-sm" /></div>
              <div><Label className="text-xs">Nom</Label><Input value={newPlan.label} onChange={(e) => setNewPlan({ ...newPlan, label: e.target.value })} placeholder="Agency" className="mt-1 rounded-sm text-sm" /></div>
              <div><Label className="text-xs">Prix affiché</Label><Input value={newPlan.priceLabel} onChange={(e) => setNewPlan({ ...newPlan, priceLabel: e.target.value })} placeholder="149€/mois" className="mt-1 rounded-sm text-sm" /></div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {NUM_FIELDS.map(({ k, label }) => (
                <div key={k}><Label className="text-xs">{label}</Label><Input type="number" value={newPlan[k]} onChange={(e) => setNewPlan({ ...newPlan, [k]: e.target.value })} className="mt-1 rounded-sm text-sm" /></div>
              ))}
            </div>
            <div><Label className="text-xs">Features (séparées par des virgules)</Label><Input value={newPlan.features} onChange={(e) => setNewPlan({ ...newPlan, features: e.target.value })} className="mt-1 rounded-sm text-sm font-mono" /></div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={newPlan.isPublic} onChange={(e) => setNewPlan({ ...newPlan, isPublic: e.target.checked })} /> Visible dans les cartes d'abonnement</label>
            <div className="flex gap-2"><Button size="sm" className="rounded-sm" onClick={createPlan}>Créer</Button><Button size="sm" variant="ghost" className="rounded-sm" onClick={() => setShowNewPlan(false)}>Annuler</Button></div>
          </div>
        )}

        <div className="space-y-4">
          {plans.map((p) => {
            const d = planDrafts[p.tier] || {};
            return (
              <div key={p.tier} className="border border-border rounded-sm p-4 space-y-3" data-testid={`plan-${p.tier}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">{p.tier}</span>
                    <Input value={d.label || ""} onChange={(e) => draftField(p.tier, "label", e.target.value)} className="h-8 rounded-sm text-sm font-semibold w-40" />
                    <Input value={d.priceLabel || ""} onChange={(e) => draftField(p.tier, "priceLabel", e.target.value)} placeholder="prix" className="h-8 rounded-sm text-sm w-28" />
                  </div>
                  <button onClick={() => deletePlan(p.tier)} className="text-muted-foreground hover:text-destructive p-1" title="Supprimer"><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                  {NUM_FIELDS.map(({ k, label }) => (
                    <div key={k}><Label className="text-[11px] text-muted-foreground">{label}</Label><Input type="number" value={d[k] ?? 0} onChange={(e) => draftField(p.tier, k, e.target.value)} className="mt-0.5 h-8 rounded-sm text-sm" /></div>
                  ))}
                </div>
                <div><Label className="text-[11px] text-muted-foreground">Features</Label><Input value={d.features || ""} onChange={(e) => draftField(p.tier, "features", e.target.value)} className="mt-0.5 h-8 rounded-sm text-sm font-mono" /></div>
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!d.isPublic} onChange={(e) => draftField(p.tier, "isPublic", e.target.checked)} /> Visible (cartes d'abonnement)</label>
                  <Button size="sm" className="rounded-sm" onClick={() => savePlan(p.tier)} disabled={savingPlan === p.tier} data-testid={`save-plan-${p.tier}`}>
                    {savingPlan === p.tier ? "…" : "Enregistrer"}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Statut clé Stripe plateforme */}
      <div className="bg-card border border-border rounded-sm p-6 mb-6 space-y-3">
        <div className="flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-primary" />
          <h3 className="font-heading text-lg font-semibold">Stripe — Clé plateforme</h3>
        </div>
        <div className="flex items-center gap-3">
          <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-sm font-semibold border ${
            stripeKeySet ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-muted text-muted-foreground border-transparent"
          }`}>
            {stripeKeySet ? <><Check className="w-3 h-3" /> Configurée</> : "Non configurée"}
          </span>
          <p className="text-sm text-muted-foreground">
            La clé <code className="font-mono text-xs bg-muted px-1 rounded">STRIPE_API_KEY</code> est définie dans le fichier <code className="font-mono text-xs bg-muted px-1 rounded">.env</code> du serveur.
          </p>
        </div>
      </div>

      {/* Price IDs par plan */}
      <div className="bg-card border border-border rounded-sm p-6 space-y-6">
        <div>
          <h3 className="font-heading text-lg font-semibold mb-1">Price IDs Stripe par forfait</h3>
          <p className="text-sm text-muted-foreground">Créez les produits dans votre dashboard Stripe puis collez les IDs ici (<code className="font-mono text-xs">price_xxx</code>).</p>
        </div>

        {["pro", "business", "enterprise"].map((plan) => (
          <div key={plan} className="space-y-3">
            <h4 className="text-sm font-semibold capitalize border-b border-border pb-2">{PLAN_LABELS[plan]}</h4>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Mensuel</Label>
                <Input
                  value={form[`stripe_price_${plan}_monthly`]}
                  onChange={upd(`stripe_price_${plan}_monthly`)}
                  placeholder="price_xxx"
                  className="mt-1.5 rounded-sm font-mono text-xs"
                />
              </div>
              <div>
                <Label>Annuel</Label>
                <Input
                  value={form[`stripe_price_${plan}_yearly`]}
                  onChange={upd(`stripe_price_${plan}_yearly`)}
                  placeholder="price_xxx"
                  className="mt-1.5 rounded-sm font-mono text-xs"
                />
              </div>
            </div>
          </div>
        ))}

        {/* Webhook secret */}
        <div className="pt-4 border-t border-border space-y-3">
          <h4 className="text-sm font-semibold">Secret webhook abonnements</h4>
          <p className="text-sm text-muted-foreground">
            URL du webhook à renseigner dans Stripe : <code className="font-mono text-xs bg-muted px-1 rounded">https://app.acteio.com/api/companies/webhook/billing</code><br />
            Événements à activer : <code className="font-mono text-xs">customer.subscription.*</code>, <code className="font-mono text-xs">invoice.payment_failed</code>
          </p>
          <div>
            <Label>
              Signing secret (whsec_…)
              {webhookSecretSet && <span className="ml-2 text-emerald-600 text-xs font-normal">● Configuré</span>}
            </Label>
            <Input
              type="password"
              value={form.stripe_billing_webhook_secret}
              onChange={upd("stripe_billing_webhook_secret")}
              placeholder={webhookSecretSet ? "Laisser vide pour conserver l'actuel" : "whsec_xxx"}
              className="mt-1.5 rounded-sm"
            />
          </div>
        </div>

        <Button onClick={save} disabled={saving} className="rounded-sm bg-primary hover:bg-primary/90 font-semibold">
          {saving ? "Enregistrement…" : "Enregistrer la configuration"}
        </Button>
      </div>

      {/* Codes promo */}
      <div className="bg-card border border-border rounded-sm p-6 mt-6 space-y-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Tag className="w-5 h-5 text-primary" />
            <h3 className="font-heading text-lg font-semibold">Codes promo</h3>
          </div>
          <Button size="sm" variant="outline" className="rounded-sm gap-1.5" onClick={() => setShowPromoForm(!showPromoForm)}>
            <Plus className="w-3.5 h-3.5" />
            Nouveau code
          </Button>
        </div>

        {showPromoForm && (
          <div className="bg-muted/40 border border-border rounded-sm p-4 space-y-3">
            <h4 className="text-sm font-semibold">Créer un code</h4>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Code *</Label>
                <Input value={promoForm.code} onChange={e => setPromoForm({ ...promoForm, code: e.target.value.toUpperCase() })}
                  placeholder="CLEYA2026" className="mt-1 rounded-sm font-mono uppercase" />
              </div>
              <div>
                <Label>Forfait accordé</Label>
                <select value={promoForm.plan} onChange={e => setPromoForm({ ...promoForm, plan: e.target.value })}
                  className="mt-1 w-full h-9 rounded-sm border border-input bg-background px-3 text-sm">
                  <option value="pro">Pro</option>
                  <option value="business">Business</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>
              <div>
                <Label>Durée (mois, 0 = permanent)</Label>
                <Input type="number" min="0" value={promoForm.durationMonths}
                  onChange={e => setPromoForm({ ...promoForm, durationMonths: parseInt(e.target.value) || 0 })}
                  className="mt-1 rounded-sm" />
              </div>
              <div>
                <Label>Max utilisations (0 = illimité)</Label>
                <Input type="number" min="0" value={promoForm.maxUses}
                  onChange={e => setPromoForm({ ...promoForm, maxUses: parseInt(e.target.value) || 0 })}
                  className="mt-1 rounded-sm" />
              </div>
              <div>
                <Label>Type de réduction</Label>
                <select value={promoForm.discountType} onChange={e => setPromoForm({ ...promoForm, discountType: e.target.value })}
                  className="mt-1 w-full border border-border rounded-sm px-3 py-2 text-sm bg-background">
                  <option value="">Aucune (forfait offert)</option>
                  <option value="percentage">Pourcentage (%)</option>
                  <option value="fixed">Montant fixe (€)</option>
                </select>
              </div>
              <div>
                <Label>Valeur de la réduction</Label>
                <div className="relative mt-1">
                  <Input type="number" min="0" value={promoForm.discountValue} disabled={!promoForm.discountType}
                    onChange={e => setPromoForm({ ...promoForm, discountValue: parseFloat(e.target.value) || 0 })}
                    className="rounded-sm pr-8 disabled:opacity-50" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">{promoForm.discountType === 'fixed' ? '€' : '%'}</span>
                </div>
              </div>
              <div className="sm:col-span-2">
                <Label>Description (interne)</Label>
                <Input value={promoForm.description} onChange={e => setPromoForm({ ...promoForm, description: e.target.value })}
                  placeholder="Code pour les clients Cleya – 1 an Business" className="mt-1 rounded-sm" />
              </div>
            </div>
            {promoForm.discountType && (
              <p className="text-xs text-muted-foreground">Code-réduction : la remise de {promoForm.discountValue}{promoForm.discountType === 'fixed' ? ' €' : ' %'} s'applique au paiement Stripe à la souscription (le forfait n'est pas offert).</p>
            )}
            <div className="flex gap-2 pt-1">
              <Button size="sm" onClick={createPromo} disabled={addingPromo} className="rounded-sm bg-primary hover:bg-primary/90 font-semibold">
                {addingPromo ? "Création…" : "Créer le code"}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowPromoForm(false)} className="rounded-sm">Annuler</Button>
            </div>
          </div>
        )}

        {promos.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aucun code promo.</p>
        ) : (
          <div className="divide-y divide-border">
            {promos.map(p => (
              <div key={p.code} className="py-3 flex items-center gap-3">
                <code className="font-mono font-semibold text-sm min-w-[120px]">{p.code}</code>
                {p.discountValue > 0 ? (
                  <span className="text-xs px-2 py-0.5 rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold">
                    -{p.discountValue}{p.discountType === 'fixed' ? ' €' : ' %'}
                  </span>
                ) : (
                  <>
                    <span className="text-xs px-2 py-0.5 rounded-sm bg-primary/10 text-primary border border-primary/20 font-semibold capitalize">{p.plan}</span>
                    <span className="text-xs text-muted-foreground">{p.durationMonths > 0 ? `${p.durationMonths} mois` : 'Permanent'}</span>
                  </>
                )}
                <span className="text-xs text-muted-foreground">{p.usedCount}{p.maxUses > 0 ? `/${p.maxUses}` : ''} use{p.usedCount !== 1 ? 's' : ''}</span>
                {p.description && <span className="text-xs text-muted-foreground flex-1 truncate">{p.description}</span>}
                {!p.description && <span className="flex-1" />}
                <button onClick={() => deletePromo(p.code)} className="text-muted-foreground hover:text-destructive transition-colors ml-auto shrink-0">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
